import React, { useState, useEffect } from 'react';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import { 
  DollarSign, 
  Calculator as CalculatorIcon, 
  Calendar, 
  Percent, 
  TrendingDown,
  Info,
  Clock,
  Save,
  PieChart
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../components/SEOHead';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';

const EarlyPayoffCalculator: React.FC = () => {
  // State for form inputs
  const [currentBalance, setCurrentBalance] = useState('25000');
  const [interestRate, setInterestRate] = useState('5.99');
  const [monthlyPayment, setMonthlyPayment] = useState('500');
  const [additionalPayment, setAdditionalPayment] = useState('0');
  const [lumpSumPayment, setLumpSumPayment] = useState('0');
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');
  // Early termination cost. Australian car loans commonly charge an early
  // repayment / break fee plus a discharge fee, and the interest saved is only
  // a real gain once those are netted off — so we model it rather than just
  // warning about it.
  const [exitFee, setExitFee] = useState('0');
  
  // State for calculation results
  const [originalMonths, setOriginalMonths] = useState(0);
  const [newMonths, setNewMonths] = useState(0);
  const [originalInterest, setOriginalInterest] = useState(0);
  const [newInterest, setNewInterest] = useState(0);
  const [totalSaved, setTotalSaved] = useState(0);
  const [monthsSaved, setMonthsSaved] = useState(0);
  // Interest saved minus the early-exit cost. This is the number that actually
  // answers "is paying this out early worth it?" — it can be negative.
  const [netSaved, setNetSaved] = useState(0);
  const [originalAmortizationSchedule, setOriginalAmortizationSchedule] = useState<Array<{
    period: number;
    payment: number;
    principal: number;
    interest: number;
    balance: number;
  }>>([]);
  const [newAmortizationSchedule, setNewAmortizationSchedule] = useState<Array<{
    period: number;
    payment: number;
    principal: number;
    interest: number;
    balance: number;
  }>>([]);
  const [comparisonChartData, setComparisonChartData] = useState<Array<{
    name: string;
    original: number;
    withExtra: number;
  }>>([]);

  // Calculate results on input change
  useEffect(() => {
    calculatePayoff();
  }, [currentBalance, interestRate, monthlyPayment, additionalPayment, lumpSumPayment, paymentFrequency, exitFee]);

  // Calculate payoff scenarios
  const calculatePayoff = () => {
    const balance = parseFloat(currentBalance);
    const rate = parseFloat(interestRate);
    const payment = parseFloat(monthlyPayment);
    const additional = parseFloat(additionalPayment || '0');
    const lumpSum = parseFloat(lumpSumPayment || '0');
    
    if (isNaN(balance) || balance <= 0 || isNaN(rate) || isNaN(payment) || payment <= 0) {
      return;
    }
    
    const monthlyRate = rate / 100 / 12;
    
    // Original schedule (without extra payments)
    const original = generateAmortizationSchedule(
      balance, 
      monthlyRate, 
      payment,
      0, // No additional payment
      0  // No lump sum
    );
    
    // New schedule (with extra payments)
    const withExtra = generateAmortizationSchedule(
      balance, 
      monthlyRate, 
      payment,
      additional,
      lumpSum
    );
    
    setOriginalAmortizationSchedule(original.schedule);
    setNewAmortizationSchedule(withExtra.schedule);
    
    setOriginalMonths(original.months);
    setNewMonths(withExtra.months);
    setOriginalInterest(original.totalInterest);
    setNewInterest(withExtra.totalInterest);
    const interestSaved = original.totalInterest - withExtra.totalInterest;
    const fee = parseFloat(exitFee || '0');
    setTotalSaved(interestSaved);
    setMonthsSaved(original.months - withExtra.months);
    setNetSaved(interestSaved - (isNaN(fee) ? 0 : fee));
    
    // Generate comparison chart data
    generateComparisonChart(original.schedule, withExtra.schedule);
  };

  // Generate amortization schedule
  const generateAmortizationSchedule = (
    principal: number, 
    monthlyRate: number, 
    monthlyPayment: number,
    additionalPayment: number = 0,
    lumpSumPayment: number = 0
  ) => {
    let balance = principal;
    let totalInterest = 0;
    const schedule = [];
    let period = 0;
    
    // Apply lump sum payment at the beginning if provided
    if (lumpSumPayment > 0) {
      if (lumpSumPayment >= balance) {
        // Lump sum covers entire balance
        return {
          schedule: [{
            period: 1,
            payment: balance,
            principal: balance,
            interest: 0,
            balance: 0
          }],
          months: 1,
          totalInterest: 0
        };
      } else {
        balance -= lumpSumPayment;
      }
    }
    
    // Calculate regular payments with additional payment if specified
    while (balance > 0) {
      period++;
      
      // Calculate interest for this period
      const interestPayment = balance * monthlyRate;
      
      // Total payment including any additional payment
      const totalPaymentThisPeriod = Math.min(monthlyPayment + additionalPayment, balance + interestPayment);
      
      // Calculate principal portion
      const principalPayment = totalPaymentThisPeriod - interestPayment;
      
      // Update balance
      balance -= principalPayment;
      
      // Track total interest paid
      totalInterest += interestPayment;
      
      // Add to schedule
      schedule.push({
        period,
        payment: totalPaymentThisPeriod,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, balance)
      });
      
      // Safety check for very long loans or tiny payments
      if (period > 1000) {
        break;
      }
    }
    
    return {
      schedule,
      months: period,
      totalInterest
    };
  };

  // Generate comparison chart data
  const generateComparisonChart = (originalSchedule: any[], newSchedule: any[]) => {
    const maxPeriods = Math.max(
      originalSchedule.length, 
      newSchedule.length
    );
    
    // Group data by year for better visualization
    const yearlyData = [];
    const yearsToShow = Math.ceil(maxPeriods / 12);
    
    for (let year = 1; year <= yearsToShow; year++) {
      const originalBalanceAtYearEnd = 
        year * 12 <= originalSchedule.length 
          ? originalSchedule[year * 12 - 1].balance 
          : 0;
      
      const newBalanceAtYearEnd = 
        year * 12 <= newSchedule.length 
          ? newSchedule[year * 12 - 1].balance 
          : 0;
      
      yearlyData.push({
        name: `Year ${year}`,
        original: originalBalanceAtYearEnd,
        withExtra: newBalanceAtYearEnd
      });
    }
    
    setComparisonChartData(yearlyData);
  };

  // Format currency
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Handle payment frequency change (for display purposes)
  const getAdjustedPayment = (baseAmount: number): string => {
    if (baseAmount === 0) return formatCurrency(0);
    
    switch(paymentFrequency) {
      case 'weekly':
        return formatCurrency(baseAmount * 12 / 52);
      case 'fortnightly':
        return formatCurrency(baseAmount * 12 / 26);
      case 'monthly':
      default:
        return formatCurrency(baseAmount);
    }
  };

  return (
    <>
      <SEOHead
        title="Car Loan Early Payoff Calculator Australia | Calculate Your Savings"
        description="Calculate how much interest you can save by paying off your car loan early. See the impact of extra payments and lump sums on your total interest and loan term."
        keywords="car loan early payoff calculator, pay off car loan early, extra car loan payments, lump sum car payment, car loan interest savings, australia car loan calculator"
      />
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Car Loan Early Payoff Calculator",
              "description": "Calculate how much interest and time you can save by paying off your car loan early.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": {
                "@type": "Country",
                "name": "Australia"
              },
              "interactivityType": "active"
            }
          `}
        </script>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Is it worth paying off a car loan early in Australia?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "It is worth it when the interest you avoid is greater than the cost of exiting the loan. Work out the interest saved by paying early, then subtract any early repayment or break fee and the discharge fee shown on your contract. If the result is positive you are ahead; if it is negative, paying out early costs you money even though you clear the debt sooner. Fixed-rate contracts usually cost more to exit than variable-rate ones."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What is a car loan payout figure?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "A payout figure is the exact amount required to clear your loan in full on a specified date. It includes the remaining principal, interest accrued to that date, and any early repayment, break or discharge fees. Because it is date-specific it expires, so request a fresh figure from your lender before you settle."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do extra repayments reduce the principal on a car loan?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Only if your lender applies them that way. Some credit providers hold extra repayments in advance against future scheduled repayments rather than reducing the balance immediately, which produces no interest saving. Confirm with your lender that additional repayments are applied directly to the principal, and check whether a cap on fee-free extra repayments applies."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Does paying fortnightly instead of monthly pay off a car loan faster?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, if you pay half the monthly amount every fortnight. There are 26 fortnights in a year, so you make the equivalent of 13 monthly repayments instead of 12. That extra repayment goes against the principal and shortens the loan, provided your lender allows fortnightly repayments and applies the surplus to the balance."
                  }
                },
                {
                  "@type": "Question",
                  "name": "When does a lump sum save the most interest on a car loan?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Earliest in the term. Interest is charged on the outstanding balance, so the balance is highest and the interest saving largest at the start. The same lump sum applied in the final year of a loan saves only a fraction of what it would have saved in the first year, because there is little remaining interest left to avoid."
                  }
                }
              ]
            }
          `}
        </script>
      </Helmet>
      <Calculator
        title="Australian Car Loan Early Payoff Calculator"
        description="Calculate how much interest and time you can save by making extra payments or paying off your car loan early. See the potential savings from different payoff strategies."
        icon={CalculatorIcon}
        result={newMonths > 0 ? `You save: ${formatCurrency(totalSaved)} and ${monthsSaved} ${monthsSaved === 1 ? 'month' : 'months'}` : undefined}
      >
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-700">
                Making extra repayments or a lump sum payment can significantly reduce the interest you pay and the time it takes to pay off your loan. This calculator shows you exactly how much you can save.
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Current Loan Balance */}
            <Input
              label="Current Loan Balance"
              value={currentBalance}
              onChange={setCurrentBalance}
              unit="$"
              placeholder="e.g. 25000"
            />
            
            {/* Interest Rate */}
            <Input
              label="Interest Rate"
              value={interestRate}
              onChange={setInterestRate}
              unit="%"
              placeholder="e.g. 5.99"
            />
            
            {/* Regular Monthly Payment */}
            <Input
              label="Regular Monthly Payment"
              value={monthlyPayment}
              onChange={setMonthlyPayment}
              unit="$"
              placeholder="e.g. 500"
            />
            
            {/* Payment Frequency */}
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Frequency</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={paymentFrequency}
                onChange={(e) => setPaymentFrequency(e.target.value)}
              >
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            
            {/* Additional Payment (per period) */}
            <Input
              label="Additional Payment (per period)"
              value={additionalPayment}
              onChange={setAdditionalPayment}
              unit="$"
              placeholder="e.g. 100"
            />
            
            {/* One-Time Lump Sum Payment */}
            <Input
              label="One-Time Lump Sum Payment"
              value={lumpSumPayment}
              onChange={setLumpSumPayment}
              unit="$"
              placeholder="e.g. 5000"
            />

            {/* Early Exit / Break Fee */}
            <Input
              label="Early Exit & Discharge Fees"
              value={exitFee}
              onChange={setExitFee}
              unit="$"
              placeholder="e.g. 350"
            />
          </div>

          <p className="text-sm text-gray-500 -mt-2">
            Your exit cost is on your loan contract, usually as an early repayment or break fee plus a
            discharge/settlement fee. Fixed-rate contracts typically cost more to exit than variable ones. If you
            are not sure, ask your lender for a written payout figure — it states the exact amount to clear the
            loan on a given date, including any fees.
          </p>

          {/* Results Section */}
          {newMonths > 0 && (
            <div className="mt-8 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-blue-600 text-white px-6 py-4">
                <h3 className="text-xl font-semibold">Early Payoff Summary</h3>
              </div>
              
              <div className="p-6">
                {/* Is it actually worth it? Interest saved is only a real gain
                    once the early-exit cost is netted off. */}
                {parseFloat(exitFee || '0') > 0 && (
                  <div
                    className={`mb-8 p-4 rounded-lg border ${
                      netSaved > 0
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <h4
                      className={`text-lg font-semibold mb-2 ${
                        netSaved > 0 ? 'text-green-800' : 'text-red-800'
                      }`}
                    >
                      {netSaved > 0
                        ? `Worth it: you are ${formatCurrency(netSaved)} ahead`
                        : `Not worth it: you are ${formatCurrency(Math.abs(netSaved))} behind`}
                    </h4>
                    <div className={`text-sm space-y-1 ${netSaved > 0 ? 'text-green-800' : 'text-red-800'}`}>
                      <div className="flex justify-between">
                        <span>Interest saved</span>
                        <span className="font-medium">{formatCurrency(totalSaved)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Less early exit &amp; discharge fees</span>
                        <span className="font-medium">-{formatCurrency(parseFloat(exitFee || '0'))}</span>
                      </div>
                      <div className="flex justify-between border-t pt-1 mt-1 font-semibold">
                        <span>Net benefit</span>
                        <span>{formatCurrency(netSaved)}</span>
                      </div>
                    </div>
                    <p className={`text-sm mt-3 ${netSaved > 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {netSaved > 0
                        ? `After paying ${formatCurrency(parseFloat(exitFee || '0'))} to exit, you still keep ${formatCurrency(netSaved)} of the interest you avoided, and finish ${monthsSaved} ${monthsSaved === 1 ? 'month' : 'months'} sooner.`
                        : `The exit cost of ${formatCurrency(parseFloat(exitFee || '0'))} is more than the ${formatCurrency(totalSaved)} of interest you would avoid. On these numbers, paying out early costs you money — though you would still clear the debt ${monthsSaved} ${monthsSaved === 1 ? 'month' : 'months'} sooner.`}
                    </p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">Original Loan</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Time to payoff:</span>
                        <span className="font-medium">{originalMonths} months ({Math.floor(originalMonths / 12)} years, {originalMonths % 12} months)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total interest:</span>
                        <span className="font-medium">{formatCurrency(originalInterest)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Monthly payment:</span>
                        <span className="font-medium">{getAdjustedPayment(parseFloat(monthlyPayment))}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="text-lg font-semibold text-blue-800 mb-4">With Extra Payments</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-blue-700">Time to payoff:</span>
                        <span className="font-medium">{newMonths} months ({Math.floor(newMonths / 12)} years, {newMonths % 12} months)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Total interest:</span>
                        <span className="font-medium">{formatCurrency(newInterest)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">New payment:</span>
                        <span className="font-medium">{getAdjustedPayment(parseFloat(monthlyPayment) + parseFloat(additionalPayment))}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg mb-8">
                  <h4 className="text-lg font-semibold text-green-800 mb-3">Your Savings</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white p-3 rounded border border-green-100 flex items-center">
                      <Save className="h-8 w-8 text-green-500 mr-3" />
                      <div>
                        <div className="text-sm text-gray-600">Interest Saved</div>
                        <div className="text-xl font-bold text-green-600">{formatCurrency(totalSaved)}</div>
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded border border-green-100 flex items-center">
                      <Clock className="h-8 w-8 text-green-500 mr-3" />
                      <div>
                        <div className="text-sm text-gray-600">Time Saved</div>
                        <div className="text-xl font-bold text-green-600">
                          {monthsSaved} {monthsSaved === 1 ? 'month' : 'months'} 
                          {monthsSaved >= 12 ? ` (${Math.floor(monthsSaved / 12)} years, ${monthsSaved % 12} months)` : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Chart */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold mb-4">Balance Comparison</h4>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={comparisonChartData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                        <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, ""]} />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="original" 
                          name="Original Schedule" 
                          stroke="#3B82F6" 
                          strokeWidth={2} 
                          dot={{ r: 4 }} 
                        />
                        <Line 
                          type="monotone" 
                          dataKey="withExtra" 
                          name="With Extra Payments" 
                          stroke="#10B981" 
                          strokeWidth={2} 
                          dot={{ r: 4 }} 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                {/* Amortization Schedule */}
                <div>
                  <h4 className="text-lg font-semibold mb-4">New Payoff Schedule</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Principal</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interest</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining Balance</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {newAmortizationSchedule.slice(0, 12).map((row) => (
                          <tr key={row.period}>
                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{row.period}</td>
                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.payment)}</td>
                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.principal)}</td>
                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.interest)}</td>
                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.balance)}</td>
                          </tr>
                        ))}
                        {newAmortizationSchedule.length > 12 && (
                          <tr>
                            <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                              Showing first year only. Full schedule available upon request.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Educational Section */}
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Benefits of Paying Your Car Loan Early</h3>
            <p className="text-gray-600 mb-4">
              Making extra payments on your car loan can provide several financial advantages:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-700 mb-2">Save on Interest</h4>
                <p className="text-gray-600">
                  Every extra dollar you pay reduces your principal balance immediately, which means you'll pay less interest over the life of your loan.
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-700 mb-2">Become Debt-Free Sooner</h4>
                <p className="text-gray-600">
                  Extra payments can significantly reduce your loan term, helping you achieve debt freedom months or even years ahead of schedule.
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-700 mb-2">Improve Cash Flow</h4>
                <p className="text-gray-600">
                  Once your loan is paid off, you'll free up the money that was going toward monthly payments for other financial goals.
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-700 mb-2">Reduce Financial Stress</h4>
                <p className="text-gray-600">
                  Eliminating debt can reduce financial anxiety and give you more flexibility in your budget and financial planning.
                </p>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
              <h4 className="font-semibold text-gray-700 mb-2">Extra Payment Strategies</h4>
              <p className="text-gray-600 mb-3">
                There are several effective approaches to paying off your car loan early:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-gray-600">
                <li><strong>Regular additional repayments:</strong> Add a fixed amount to each scheduled repayment</li>
                <li><strong>Fortnightly repayments:</strong> Pay half your monthly repayment every fortnight — 26 half-payments a year is the equivalent of 13 monthly ones, so you make one extra repayment a year without noticing it</li>
                <li><strong>Annual lump sums:</strong> Apply tax refunds, bonuses, or other windfalls to your principal</li>
                <li><strong>Round up repayments:</strong> Round your repayment up to the nearest $50 or $100</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
              <h4 className="font-semibold text-yellow-800 mb-2">Important Considerations</h4>
              <ul className="list-disc pl-5 space-y-1 text-yellow-800">
                <li>Check if your loan has early repayment fees or penalties before making extra payments</li>
                <li>Contact your lender to ensure extra payments are applied to the principal, not future interest</li>
                <li>Consider if you have higher-interest debt that should be prioritized first</li>
                <li>Maintain an emergency fund before dedicating extra money to loan repayment</li>
              </ul>
            </div>
          </div>
        </div>
      </Calculator>
    </>
  );
};

export default EarlyPayoffCalculator; 